/**
 * pickup-countdown-cr.js
 * Estado del retiro en tienda (BOPIS 30 min) para la tienda del visitante.
 *
 * Las tiendas cierran entre las 18:00 y las 21:00, así que el corte del retiro
 * es por tienda: cierre - margen (30 min). Este módulo solo calcula; el texto
 * lo arma announcement-delivery-countdown.js.
 *
 * Qué tienda:
 *   1. localStorage.preferredStoreId (la elegida en el locator), exacta.
 *   2. Cookie location_position (prompt de ubicación aceptado): la que el
 *      locator pintó en el header (localStorage.ishopNearestStoreId). En San José
 *      hay tiendas a ~1 km, así que no se recalcula aparte: la barra y el header
 *      dicen la misma. Si el locator aún no la guardó, la más cercana de la
 *      tabla por distancia, si está a <= MAX_KM.
 *   3. Nada: null, y la barra deja el sufijo fijo.
 *
 * Horario: las tiendas abren en feriado con su horario normal de ese día de la
 * semana. Solo cierran las fechas de retiroCerrado (25-dic y 1-ene).
 *
 * Pasado el corte de la tienda: si otra tienda de la tabla a <= retiroAlternativaKm
 * de la elegida sigue abierta hoy, va en `alternativa` (la más cercana).
 *
 * Requiere coverage-gam.js antes (reusa sus coordenadas y su reloj de CR).
 *
 * window.ANNOUNCEMENT_CONFIG:
 *   - retiroHorarios: líneas "ID | Nombre | Lu-Sá 10-21 | Do 10-20" (ver schema)
 *   - retiroCerrado:  fechas con todas las tiendas cerradas: MM-DD (cada año)
 *                     o AAAA-MM-DD (una vez)
 *   - retiroMargenMin, retiroUmbralCountdownHoras, retiroUmbralUrgenteMin
 *   - retiroAlternativaKm: radio para ofrecer otra tienda abierta (0 = no)
 *
 * window.iShopPickup.detalle(now) -> null | {
 *   tienda: { id, nombre }, origen: 'tienda' | 'gps',
 *   estado: 'abre'      antes de abrir hoy             (label 'HOY', hora)
 *         | 'abierta'   falta más que el umbral
 *         | 'countdown' faltan <= umbral              (segundosRestantes)
 *         | 'urgente'   faltan <= umbral urgente      (segundosRestantes)
 *         | 'cerrada'   pasado el corte o cerrada hoy (label, hora del próximo día)
 *   alternativa?: { tienda: { id, nombre }, km, estado, segundosRestantes }
 *                 solo con estado 'cerrada' y otra tienda abierta hoy
 * }
 */
(function () {
  'use strict';

  var CFG = window.ANNOUNCEMENT_CONFIG || {};
  var COV = window.iShopCoverage;
  if (!COV || !COV._partsCR) return;

  function num(v, def) {
    var n = parseFloat(v);
    return isFinite(n) ? n : def;
  }

  var MARGEN = num(CFG.retiroMargenMin, 30) * 60;
  var UMBRAL_COUNTDOWN = num(CFG.retiroUmbralCountdownHoras, 3) * 3600;
  var UMBRAL_URGENTE = num(CFG.retiroUmbralUrgenteMin, 30) * 60;
  var MAX_KM = 40;
  var ALT_KM = num(CFG.retiroAlternativaKm, 15);

  var DIAS = ['DOMINGO', 'LUNES', 'MARTES', 'MIÉRCOLES', 'JUEVES', 'VIERNES', 'SÁBADO'];

  function lineas(v) {
    if (Array.isArray(v)) return v;
    return String(v || '').split(/\r?\n|<br\s*\/?>/);
  }

  function fechas(v) {
    var map = {};
    lineas(v).forEach(function (raw) {
      var s = String(raw).trim();
      if (/^(\d{4}-)?\d{2}-\d{2}$/.test(s)) map[s] = 1;
    });
    return map;
  }

  // "10-21", "10am-9pm", "9:30-20:30" -> [inicio, fin] en segundos del día.
  function rango(s) {
    var m = String(s || '').match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\s*-\s*(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i);
    if (!m) return null;
    function seg(h, mi, ap) {
      h = parseInt(h, 10);
      if (ap && /pm/i.test(ap) && h < 12) h += 12;
      if (ap && /am/i.test(ap) && h === 12) h = 0;
      return h * 3600 + (mi ? parseInt(mi, 10) * 60 : 0);
    }
    var a = seg(m[1], m[2], m[3]);
    var b = seg(m[4], m[5], m[6]);
    return b > a ? [a, b] : null;
  }

  // "ID | Nombre | Lu-Sá 10-21 | Do 10-20"
  var TIENDAS = {};
  lineas(CFG.retiroHorarios).forEach(function (raw) {
    var c = String(raw).split('|').map(function (x) { return x.trim(); });
    if (c.length < 4 || !/^\d+$/.test(c[0])) return;
    var semana = rango(c[2].replace(/^[^\d]*/, ''));
    var domingo = rango(c[3].replace(/^[^\d]*/, ''));
    if (!semana || !domingo) return;
    TIENDAS[c[0]] = { id: c[0], nombre: c[1], semana: semana, domingo: domingo };
  });

  var CERRADO = fechas(CFG.retiroCerrado);

  // Feriado = día normal: abre con el horario de su día de la semana.
  function horario(t, ymd, dow) {
    if (CERRADO[ymd] || CERRADO[ymd.slice(5)]) return null;
    return dow === 0 ? t.domingo : t.semana;
  }

  function storeNumId(raw) {
    if (!raw) return null;
    var m = String(raw).match(/(\d+)\s*$/);
    return m ? m[1] : null;
  }

  function getCookie(name) {
    var m = document.cookie.match('(?:^|; )' + name + '=([^;]*)');
    return m ? decodeURIComponent(m[1]) : null;
  }

  function km(lat1, lng1, lat2, lng2) {
    var r = Math.PI / 180;
    var dLat = (lat2 - lat1) * r, dLng = (lng2 - lng1) * r;
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * r) * Math.cos(lat2 * r) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function leer(k) {
    try { return storeNumId(window.localStorage && localStorage.getItem(k)); } catch (e) { return null; }
  }

  function tienda() {
    var id = leer('preferredStoreId');
    if (id) return TIENDAS[id] ? { t: TIENDAS[id], origen: 'tienda' } : null;

    var raw = getCookie('location_position');
    if (!raw) return null;
    var delHeader = leer('ishopNearestStoreId');
    if (delHeader && TIENDAS[delHeader]) return { t: TIENDAS[delHeader], origen: 'gps' };
    var lat, lng;
    try {
      var pos = JSON.parse(raw);
      lat = pos && pos.coords && pos.coords.latitude;
      lng = pos && pos.coords && pos.coords.longitude;
    } catch (e) { return null; }
    if (typeof lat !== 'number' || typeof lng !== 'number') return null;

    var coords = COV._stores || {};
    var mejor = null, dist = Infinity;
    Object.keys(TIENDAS).forEach(function (id) {
      var c = coords[id];
      if (!c) return;
      var d = km(lat, lng, c.lat, c.lng);
      if (d < dist) { dist = d; mejor = TIENDAS[id]; }
    });
    return (mejor && dist <= MAX_KM) ? { t: mejor, origen: 'gps' } : null;
  }

  // Primer día después de hoy en que la tienda abre.
  function proximaApertura(t, now) {
    for (var i = 1; i <= 14; i++) {
      var p = COV._partsCR(new Date(now.getTime() + i * 86400000));
      var h = horario(t, p.ymd, p.dow);
      if (h) return { label: i === 1 ? 'MAÑANA' : 'EL ' + DIAS[p.dow], hora: h[0] };
    }
    return null;
  }

  // Estado de hoy de una tienda, sin mirar días siguientes.
  function hoy(t, p) {
    var ahora = p.hour * 3600 + p.minute * 60 + p.second;
    var h = horario(t, p.ymd, p.dow);
    if (h && ahora < h[0]) return { estado: 'abre', hora: h[0] };
    if (h && ahora < h[1] - MARGEN) {
      var restan = h[1] - MARGEN - ahora;
      return {
        estado: restan <= UMBRAL_URGENTE ? 'urgente'
              : restan <= UMBRAL_COUNTDOWN ? 'countdown' : 'abierta',
        segundosRestantes: restan
      };
    }
    return null;
  }

  // La tienda más cercana a `t` que todavía recibe retiros hoy.
  function alternativa(t, p) {
    var coords = COV._stores || {};
    var o = coords[t.id];
    if (!o || !(ALT_KM > 0)) return null;
    var mejor = null;
    Object.keys(TIENDAS).forEach(function (id) {
      var c = coords[id];
      if (id === t.id || !c) return;
      var d = km(o.lat, o.lng, c.lat, c.lng);
      if (d > ALT_KM || (mejor && d >= mejor.km)) return;
      var e = hoy(TIENDAS[id], p);
      if (!e || e.estado === 'abre') return;
      mejor = { tienda: { id: id, nombre: TIENDAS[id].nombre }, km: d,
                estado: e.estado, segundosRestantes: e.segundosRestantes };
    });
    return mejor;
  }

  function detalle(now) {
    now = now || new Date();
    var elegida = tienda();
    if (!elegida) return null;
    var t = elegida.t;
    var base = { tienda: { id: t.id, nombre: t.nombre }, origen: elegida.origen };

    var p = COV._partsCR(now);
    var e = hoy(t, p);
    if (e) {
      base.estado = e.estado;
      if (e.estado === 'abre') { base.label = 'HOY'; base.hora = e.hora; }
      else base.segundosRestantes = e.segundosRestantes;
      return base;
    }
    var prox = proximaApertura(t, now);
    if (!prox) return null;
    base.estado = 'cerrada'; base.label = prox.label; base.hora = prox.hora;
    var alt = alternativa(t, p);
    if (alt) base.alternativa = alt;
    return base;
  }

  window.iShopPickup = { detalle: detalle, _tiendas: TIENDAS };
})();
